import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, UserCog, Plus, Building2, Users as UsersIcon, DollarSign, RotateCcw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';
import Badge from '../../components/shared/Badge';
import InteracoesTimeline from './components/InteracoesTimeline';

const STATUS_LABELS = { ativo: 'Ativo', morno: 'Morno', parado: 'Parado', novo: 'Novo' };
const STATUS_OPTIONS = ['novo', 'ativo', 'morno', 'parado'];

function fmtBRL(v) {
  if (v === null || v === undefined || v === '') return '—';
  return parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Colunas `date` do Postgres voltam como timestamp ISO completo ("2026-08-28T00:00:00.000Z"),
// não como "YYYY-MM-DD" puro — normaliza antes de comparar/formatar (mesmo helper de EventosList).
const soData = (iso) => (iso || '').slice(0, 10);
const fmtDataBR = (iso) => { const d = soData(iso); return d ? d.split('-').reverse().join('/') : ''; };

const TIPO_VISITA_LABELS = {
  visita_construtora: 'Visita à construtora',
  visita_cliente_realizada: 'Visita com cliente realizada',
};
const TIPO_VISITA_ICONS = { visita_construtora: Building2, visita_cliente_realizada: UsersIcon };

function fmtDataVisita(d) {
  if (!d) return '—';
  const [ano, mes, dia] = d.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : d;
}

function VisitasRegistradas({ corretorId }) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [tipo, setTipo] = useState('visita_construtora');
  const [dataVisita, setDataVisita] = useState('');
  const [observacao, setObservacao] = useState('');
  const [clienteNome, setClienteNome] = useState('');
  const [clienteTelefone, setClienteTelefone] = useState('');

  const { data: visitas, isLoading } = useQuery({
    queryKey: ['parceiro-visitas', corretorId],
    queryFn: () => api.get(`/parceiros/${corretorId}/visitas`).then(r => r.data.data),
  });

  const registrarMut = useMutation({
    mutationFn: () => api.post(`/parceiros/${corretorId}/visitas`, {
      tipo,
      data_visita: dataVisita || null,
      observacao: observacao || null,
      ...(tipo === 'visita_cliente_realizada' ? { cliente_nome: clienteNome || null, cliente_telefone: clienteTelefone || null } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiro-visitas', corretorId] });
      setModalOpen(false);
      setTipo('visita_construtora'); setDataVisita(''); setObservacao(''); setClienteNome(''); setClienteTelefone('');
    },
  });

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-700">Visitas registradas</h2>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary"
        >
          <Plus size={15} /> Registrar visita
        </button>
      </div>

      {isLoading && <div className="text-center text-gray-400 py-6 text-sm">Carregando...</div>}
      {!isLoading && (visitas || []).length === 0 && (
        <div className="text-center text-gray-400 py-6 text-sm">Nenhuma visita registrada ainda.</div>
      )}
      {!isLoading && (visitas || []).length > 0 && (
        <div className="space-y-2">
          {visitas.map(v => {
            const Icon = TIPO_VISITA_ICONS[v.tipo];
            return (
              <div key={v.id} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                <Icon size={16} className="text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800">{TIPO_VISITA_LABELS[v.tipo] || v.tipo}</span>
                    <span className="text-xs text-gray-400">{fmtDataVisita(v.payload?.data_visita)}</span>
                  </div>
                  {v.payload?.observacao && <p className="text-sm text-gray-600 mt-0.5">{v.payload.observacao}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    Registrado por {v.registrado_por_nome || '—'} · {formatDistanceToNow(new Date(v.created_at), { locale: ptBR, addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Registrar visita" size="sm">
        <form onSubmit={e => { e.preventDefault(); registrarMut.mutate(); }} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="input-field w-full">
              {Object.entries(TIPO_VISITA_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Data da visita</label>
            <input type="date" value={dataVisita} onChange={e => setDataVisita(e.target.value)} className="input-field w-full" />
          </div>
          {tipo === 'visita_cliente_realizada' && (
            <div className="space-y-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome do cliente</label>
                <input value={clienteNome} onChange={e => setClienteNome(e.target.value)} className="input-field w-full" placeholder="Nome do cliente visitante" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Telefone do cliente</label>
                <input value={clienteTelefone} onChange={e => setClienteTelefone(e.target.value)} className="input-field w-full" placeholder="Ex: 44999999999" />
              </div>
              <p className="text-xs text-blue-700">Se informado, o telefone cria ou vincula automaticamente um lead deste cliente.</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observação</label>
            <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={3} className="input-field w-full resize-y" placeholder="Detalhes opcionais sobre a visita..." />
          </div>
          {registrarMut.isError && (
            <p className="text-sm text-red-600">{registrarMut.error?.response?.data?.error || 'Erro ao registrar visita'}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" disabled={registrarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
              {registrarMut.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function VendasRegistradas({ corretorId }) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [empreendimentoId, setEmpreendimentoId] = useState('');
  const [unidadeId, setUnidadeId] = useState('');
  const [vgv, setVgv] = useState('');
  const [dataVenda, setDataVenda] = useState('');
  const [observacao, setObservacao] = useState('');
  const [clienteNome, setClienteNome] = useState('');
  const [clienteTelefone, setClienteTelefone] = useState('');
  const [clienteEmail, setClienteEmail] = useState('');

  const { data: vendas, isLoading } = useQuery({
    queryKey: ['parceiro-vendas-manuais', corretorId],
    queryFn: () => api.get(`/parceiros/${corretorId}/vendas-manuais`).then(r => r.data.data),
  });

  const { data: empreendimentos } = useQuery({
    queryKey: ['empreendimentos'],
    queryFn: () => api.get('/empreendimentos').then(r => r.data.data),
    enabled: modalOpen,
  });

  const { data: unidades } = useQuery({
    queryKey: ['unidades-venda-manual', empreendimentoId],
    queryFn: () => api.get('/unidades', { params: { empreendimento_id: empreendimentoId } }).then(r => r.data.data),
    enabled: modalOpen && !!empreendimentoId,
  });

  // Ao escolher a unidade, pré-preenche o VGV com o preço dela (editável em seguida).
  useEffect(() => {
    if (!unidadeId) return;
    const unidade = (unidades || []).find(u => u.id === unidadeId);
    if (unidade?.preco != null) setVgv(String(unidade.preco));
  }, [unidadeId, unidades]);

  function resetForm() {
    setEmpreendimentoId(''); setUnidadeId(''); setVgv(''); setDataVenda('');
    setObservacao(''); setClienteNome(''); setClienteTelefone(''); setClienteEmail('');
  }

  const registrarMut = useMutation({
    mutationFn: () => api.post(`/parceiros/${corretorId}/vendas-manuais`, {
      empreendimento_id: empreendimentoId,
      unidade_id: unidadeId || null,
      vgv,
      cliente_nome: clienteNome || null,
      cliente_telefone: clienteTelefone || null,
      cliente_email: clienteEmail || null,
      data_venda: dataVenda,
      observacao: observacao || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parceiro-vendas-manuais', corretorId] });
      setModalOpen(false);
      resetForm();
    },
  });

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-700">Vendas registradas</h2>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary"
        >
          <Plus size={15} /> Registrar venda
        </button>
      </div>

      {isLoading && <div className="text-center text-gray-400 py-6 text-sm">Carregando...</div>}
      {!isLoading && (vendas || []).length === 0 && (
        <div className="text-center text-gray-400 py-6 text-sm">Nenhuma venda registrada ainda.</div>
      )}
      {!isLoading && (vendas || []).length > 0 && (
        <div className="space-y-2">
          {vendas.map(v => (
            <div key={v.id} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              <DollarSign size={16} className="text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{v.empreendimento_nome || '—'}</span>
                  {v.unidade_identificador && <span className="text-xs text-gray-400">Unidade {v.unidade_identificador}</span>}
                  <span className="text-xs text-gray-400">{fmtDataVisita(v.data_venda)}</span>
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{fmtBRL(v.vgv)}</p>
                {v.observacao && <p className="text-sm text-gray-600 mt-0.5">{v.observacao}</p>}
                <p className="text-xs text-gray-400 mt-1">
                  Registrado por {v.registrado_por_nome || '—'} · {formatDistanceToNow(new Date(v.created_at), { locale: ptBR, addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Registrar venda" size="sm">
        <form onSubmit={e => { e.preventDefault(); registrarMut.mutate(); }} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Empreendimento</label>
            <select
              value={empreendimentoId}
              onChange={e => { setEmpreendimentoId(e.target.value); setUnidadeId(''); }}
              className="input-field w-full"
              required
            >
              <option value="">Selecione...</option>
              {(empreendimentos || []).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Unidade</label>
            <select
              value={unidadeId}
              onChange={e => setUnidadeId(e.target.value)}
              className="input-field w-full"
              disabled={!empreendimentoId}
            >
              <option value="">Não identificada / não encontrada</option>
              {(unidades || []).map(u => (
                <option key={u.id} value={u.id}>
                  {u.identificador}{u.torre ? ` - ${u.torre}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">VGV</label>
            <input type="number" step="0.01" min="0" value={vgv} onChange={e => setVgv(e.target.value)} className="input-field w-full" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Data da venda</label>
            <input type="date" value={dataVenda} onChange={e => setDataVenda(e.target.value)} className="input-field w-full" required />
          </div>
          <div className="space-y-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome do cliente</label>
              <input value={clienteNome} onChange={e => setClienteNome(e.target.value)} className="input-field w-full" placeholder="Nome do cliente comprador" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Telefone do cliente</label>
              <input value={clienteTelefone} onChange={e => setClienteTelefone(e.target.value)} className="input-field w-full" placeholder="Ex: 44999999999" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">E-mail do cliente</label>
              <input type="email" value={clienteEmail} onChange={e => setClienteEmail(e.target.value)} className="input-field w-full" />
            </div>
            <p className="text-xs text-blue-700">Se informado, o telefone cria ou vincula automaticamente um lead deste cliente.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observação</label>
            <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={3} className="input-field w-full resize-y" placeholder="Detalhes opcionais sobre a venda..." />
          </div>
          {registrarMut.isError && (
            <p className="text-sm text-red-600">{registrarMut.error?.response?.data?.error || 'Erro ao registrar venda'}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" disabled={registrarMut.isPending} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-secondary disabled:opacity-50">
              {registrarMut.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// Engajamento no portal de vendas (inbase-vendas — projeto separado, mesmo Postgres) +
// desempenho comercial real (propostas/contratos). Métricas vêm todas de GET /parceiros/:id,
// já calculadas no backend (parceiros.controller.js::getById/adicionarPontuacao).
function EngajamentoPanel({ corretor }) {
  if (!corretor) return null;
  const metricas = [
    { label: 'Pontuação', valor: corretor.pontuacao, destaque: true },
    { label: 'Logins', valor: corretor.total_logins },
    { label: 'Visualizações', valor: corretor.total_visualizacoes },
    { label: 'Simulações', valor: corretor.total_simulacoes },
    { label: 'Downloads', valor: corretor.total_downloads },
    { label: 'Eventos — inscrito', valor: corretor.total_eventos_inscrito },
    { label: 'Eventos — compareceu', valor: corretor.total_eventos_compareceu },
    { label: 'Eventos — faltou', valor: corretor.total_eventos_faltou },
    { label: 'Propostas/Reservas', valor: corretor.total_propostas },
    { label: 'Vendas', valor: corretor.total_vendas },
  ];

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <h2 className="font-semibold text-gray-700 mb-3">Engajamento &amp; Performance no Portal de Vendas</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
        {metricas.map(m => (
          <div key={m.label} className={`rounded-lg p-3 text-center ${m.destaque ? 'bg-primary/10' : 'bg-gray-50'}`}>
            <p className={`text-2xl font-bold ${m.destaque ? 'text-primary' : 'text-gray-800'}`}>{m.valor ?? 0}</p>
            <p className="text-xs text-gray-500 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-1">
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider">Valor total vendido</p>
          <p className="text-lg font-semibold text-gray-800">{fmtBRL(corretor.valor_vendido)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider">
            Comissão {corretor.comissao_percentual != null ? `(${corretor.comissao_percentual}%)` : '(% não configurado)'}
          </p>
          <p className="text-lg font-semibold text-green-700">{corretor.comissao_calculada != null ? fmtBRL(corretor.comissao_calculada) : '—'}</p>
        </div>
      </div>

      {corretor.por_empreendimento?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Interesse por empreendimento</p>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-gray-400">
              <th className="text-left py-1 font-medium">Empreendimento</th>
              <th className="text-right py-1 font-medium">Visualizações</th>
              <th className="text-right py-1 font-medium">Simulações</th>
            </tr></thead>
            <tbody>
              {corretor.por_empreendimento.map(e => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="py-1.5">{e.nome}</td>
                  <td className="py-1.5 text-right">{e.visualizacoes}</td>
                  <td className="py-1.5 text-right">{e.simulacoes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {corretor.materiais_baixados?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Materiais baixados ({corretor.materiais_baixados.length})</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {corretor.materiais_baixados.map((m, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm text-gray-600 border-b border-gray-50 py-1">
                <span className="truncate">{m.material_nome || '—'}{m.empreendimento_nome ? ` — ${m.empreendimento_nome}` : ''}</span>
                <span className="text-gray-400 text-xs shrink-0">{formatDistanceToNow(new Date(m.created_at), { locale: ptBR, addSuffix: true })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {corretor.propostas?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Propostas/Reservas ({corretor.propostas.length})</p>
          <div className="space-y-1">
            {corretor.propostas.map(p => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-sm text-gray-600 border-b border-gray-50 py-1">
                <span className="truncate">{p.cliente_nome} — {p.empreendimento_nome || '—'}{p.unidade ? ` (un. ${p.unidade})` : ''}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 shrink-0">{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {corretor.vendas?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Vendas fechadas — contratos ({corretor.vendas.length})</p>
          <div className="space-y-1">
            {corretor.vendas.map(v => (
              <div key={v.id} className="flex items-center justify-between gap-2 text-sm text-gray-600 border-b border-gray-50 py-1">
                <span className="truncate">{v.numero_contrato || '—'} — {v.empreendimento_nome || '—'}</span>
                <span className="font-medium text-gray-800 shrink-0">{fmtBRL(v.valor_total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {corretor.eventos?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Eventos ({corretor.eventos.length})</p>
          <div className="space-y-1">
            {corretor.eventos.map(ev => {
              const fim = soData(ev.data_fim || ev.data_inicio);
              const passou = fim && fim < new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
              const situacao = ev.status === 'compareceu'
                ? { label: 'Compareceu', cls: 'bg-green-50 text-green-700' }
                : passou && ev.presenca_controlada
                  ? { label: 'Faltou', cls: 'bg-red-50 text-red-600' }
                  : passou
                    ? { label: 'Confirmado', cls: 'bg-gray-100 text-gray-500' }
                    : { label: 'Inscrito', cls: 'bg-blue-50 text-blue-700' };
              const dataFmt = fmtDataBR(ev.data_inicio);
              return (
                <div key={ev.id} className="flex items-center justify-between gap-2 text-sm text-gray-600 border-b border-gray-50 py-1">
                  <span className="truncate">
                    {ev.nome}
                    {dataFmt ? <span className="text-gray-400"> — {dataFmt}</span> : null}
                    {ev.reconfirmado_em ? <span className="text-gray-400" title="reconfirmou presença no disparo"> · reconfirmou</span> : null}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${situacao.cls}`}>{situacao.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusControl({ corretorId, corretor, onChanged }) {
  const statusMut = useMutation({
    mutationFn: (status) => api.patch(`/parceiros/${corretorId}/status`, { status }),
    onSuccess: onChanged,
  });
  const autoMut = useMutation({
    mutationFn: () => api.patch(`/parceiros/${corretorId}/status/auto`),
    onSuccess: onChanged,
  });

  if (!corretor) return null;
  const isManual = corretor.status_origem === 'manual';

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <Badge label={STATUS_LABELS[corretor.status_engajamento] || corretor.status_engajamento} variant={corretor.status_engajamento} />
        <select
          value={corretor.status_engajamento}
          disabled={statusMut.isPending}
          onChange={(e) => statusMut.mutate(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>
      {isManual && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>
            Definido manualmente{corretor.status_atualizado_por_nome ? ` por ${corretor.status_atualizado_por_nome}` : ''}
            {corretor.status_atualizado_em ? ` em ${new Date(corretor.status_atualizado_em).toLocaleString('pt-BR')}` : ''}
          </span>
          <button
            onClick={() => autoMut.mutate()}
            disabled={autoMut.isPending}
            className="flex items-center gap-1 text-primary hover:underline"
          >
            <RotateCcw size={12} /> Voltar para automático
          </button>
        </div>
      )}
    </div>
  );
}

export default function ParceiroDetail() {
  const { corretorId } = useParams();
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');

  const { data: corretor } = useQuery({
    queryKey: ['corretor', corretorId],
    queryFn: () => api.get(`/parceiros/${corretorId}`).then(r => r.data.data),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['parceiro-conversa', corretorId],
    queryFn: () => api.get(`/parceiros/${corretorId}/conversas`).then(r => r.data.data),
    refetchInterval: 10000,
  });

  const conversa = data?.conversa;
  const mensagens = data?.mensagens || [];

  const takeoverMut = useMutation({
    mutationFn: (human_takeover) =>
      api.patch(`/parceiros/${corretorId}/conversas/${conversa.id}/takeover`, { human_takeover }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parceiro-conversa', corretorId] }),
  });

  const enviarMut = useMutation({
    mutationFn: (texto) =>
      api.post(`/parceiros/${corretorId}/conversas/${conversa.id}/mensagens`, { texto }),
    onSuccess: () => {
      setTexto('');
      qc.invalidateQueries({ queryKey: ['parceiro-conversa', corretorId] });
    },
  });

  function handleEnviar(e) {
    e.preventDefault();
    if (!texto.trim()) return;
    enviarMut.mutate(texto);
  }

  return (
    <div className="space-y-4">
      <Link to="/vendas/parceiros" className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary w-fit">
        <ArrowLeft size={15} /> Voltar
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{corretor?.nome || 'Carregando...'}</h1>
          <p className="text-sm text-gray-500">{corretor?.imobiliaria_nome || 'Autônomo'}</p>
        </div>
        <div className="flex items-center gap-4">
          <StatusControl
            corretorId={corretorId}
            corretor={corretor}
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ['corretor', corretorId] });
              qc.invalidateQueries({ queryKey: ['parceiros'] });
              qc.invalidateQueries({ queryKey: ['parceiros-funil'] });
            }}
          />
          {conversa && (
          <button
            onClick={() => takeoverMut.mutate(!conversa.human_takeover)}
            disabled={takeoverMut.isPending}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              conversa.human_takeover
                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserCog size={15} />
            {conversa.human_takeover ? 'Conversa assumida por você' : 'Assumir conversa'}
          </button>
          )}
        </div>
      </div>

      <EngajamentoPanel corretor={corretor} />

      <div className="bg-white rounded-xl shadow p-4 h-[60vh] flex flex-col">
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {isLoading && <div className="text-center text-gray-400 py-8">Carregando...</div>}
          {!isLoading && !conversa && (
            <div className="text-center text-gray-400 py-8">Este corretor ainda não trocou mensagens pelo WhatsApp.</div>
          )}
          {mensagens.map(m => (
            <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
                  m.direction === 'out' ? 'bg-primary text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                <p>{m.content}</p>
                <p className={`text-[10px] mt-1 ${m.direction === 'out' ? 'text-white/70' : 'text-gray-400'}`}>
                  {formatDistanceToNow(new Date(m.created_at), { locale: ptBR, addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
        </div>

        {conversa?.human_takeover && (
          <form onSubmit={handleEnviar} className="flex items-center gap-2 pt-3 border-t border-gray-100 mt-2">
            <input
              className="input-field flex-1"
              placeholder="Digite uma mensagem manual..."
              value={texto}
              onChange={e => setTexto(e.target.value)}
            />
            <button
              type="submit"
              disabled={enviarMut.isPending || !texto.trim()}
              className="p-2.5 bg-primary text-white rounded-lg hover:bg-secondary disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </form>
        )}
      </div>

      <VisitasRegistradas corretorId={corretorId} />
      <VendasRegistradas corretorId={corretorId} />
      <InteracoesTimeline corretorId={corretorId} />
    </div>
  );
}
